/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  Family,
  GetMeterUsageRequest,
  GetMeterUsageSecurity,
} from "../models/operations/getmeterusage.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildGetMeterUsageQuery,
  GetMeterUsageQueryData,
  prefetchGetMeterUsage,
  queryKeyGetMeterUsage,
} from "./getMeterUsage.core.js";
export {
  buildGetMeterUsageQuery,
  type GetMeterUsageQueryData,
  prefetchGetMeterUsage,
  queryKeyGetMeterUsage,
};

export type GetMeterUsageQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getMeterUsage usage
 *
 * @remarks
 * Get incrementally aggregated ordinary meter usage by UTC day over a maximum of three calendar months. Duplicate deliveries count unless prevented by the producer.
 */
export function useGetMeterUsage(
  request: GetMeterUsageRequest,
  security?: GetMeterUsageSecurity | undefined,
  options?: QueryHookOptions<GetMeterUsageQueryData, GetMeterUsageQueryError>,
): UseQueryResult<GetMeterUsageQueryData, GetMeterUsageQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildGetMeterUsageQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getMeterUsage usage
 *
 * @remarks
 * Get incrementally aggregated ordinary meter usage by UTC day over a maximum of three calendar months. Duplicate deliveries count unless prevented by the producer.
 */
export function useGetMeterUsageSuspense(
  request: GetMeterUsageRequest,
  security?: GetMeterUsageSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    GetMeterUsageQueryData,
    GetMeterUsageQueryError
  >,
): UseSuspenseQueryResult<GetMeterUsageQueryData, GetMeterUsageQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildGetMeterUsageQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setGetMeterUsageData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      family: Family;
      from?: Date | undefined;
      to?: Date | undefined;
      breakdown?: string | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: GetMeterUsageQueryData,
): GetMeterUsageQueryData | undefined {
  const key = queryKeyGetMeterUsage(...queryKeyBase);

  return client.setQueryData<GetMeterUsageQueryData>(key, data);
}

export function invalidateGetMeterUsage(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      family: Family;
      from?: Date | undefined;
      to?: Date | undefined;
      breakdown?: string | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "usage", "getMeterUsage", ...queryKeyBase],
  });
}

export function invalidateAllGetMeterUsage(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "usage", "getMeterUsage"],
  });
}
